"use client";

import { getTranslations } from "@/lib/i18n";
import {
  useFixedDateVoucherUnavailable,
  useVoucherSaleClosed,
} from "@/lib/use-fixed-date-voucher-unavailable";

/**
 * Sale gate for the PACKAGE deal-detail page's commercial surfaces
 * (/packages/[category]/[dealId] and its /rondreizen alias).
 *
 * The server page already `notFound()`s a closed fixed-date campaign at
 * request time (both in generateDealMetadata and in renderDealDetail), so
 * this exists for the one case that gate cannot reach: a tab that was opened
 * while the campaign was still open and is STILL OPEN when Amsterdam rolls
 * past the campaign's `booking_end_date`. Without it, that tab kept showing
 * the inline "nu vanaf" price, the "Kies datum"/"Bestel voucher" CTA, the
 * "Direct boekbaar" trust strip and the sticky bottom bar — a price plus an
 * implicit in-stock claim plus a working purchase path — for a deal whose
 * calendar API and /booking route have already closed. It also kept the
 * "Boek uiterlijk <date>" deadline line rendering as if it were still an
 * actionable instruction rather than a passed cutoff.
 *
 * Deliberately NOT a second clock: it reuses `useFixedDateVoucherUnavailable`,
 * the same shared Amsterdam business-date hook every other catalogue surface
 * (listings, voucher detail, carousels) closes on. Every gate instance on the
 * page therefore reads the same business date and flips on the same
 * date-change tick, so the shopper can never see a page that dropped its CTA
 * but kept its price (or the reverse).
 *
 * Sibling of `@/components/voucher/FixedDateVoucherSalesGate`, which does the
 * same job for the voucher DETAIL page. The two differ only in their closed
 * copy: a voucher is bought now and redeemed later ("deze voucher ... niet
 * meer te bestellen"), while this page sells a bookable arrangement, so it
 * states the product-neutral offer wording
 * (`booking.error.fixed_date_unavailable`, the same sentence the booking
 * route itself answers with). The availability LOGIC lives in neither
 * component — it lives once, in the shared hook.
 *
 * Ordinary deals are untouched: the hook returns false for them and starts no
 * clock at all, so this renders exactly its children.
 *
 * Props:
 * - `deal`             — an object carrying the campaign's date fields (both
 *                        API casings accepted); the page passes a small
 *                        date-only projection rather than the whole BFF row
 * - `locale`           — request locale, for the closed copy
 * - `showClosedNotice` — after the cutoff, replace the children with the
 *                        honest closed message instead of rendering nothing.
 *                        Exactly ONE surface on the page sets this (the
 *                        pricing block); the others simply disappear, so the
 *                        shopper reads one explanation, not four.
 * - `className`        — LAYOUT classes for that notice, appended to its own
 *                        styling rather than replacing it. The notice takes
 *                        the place of whatever it closed, so the caller needs
 *                        to hand it that slot's sizing (the pricing column's
 *                        `lg:min-w-[340px]`) without also having to restate —
 *                        and risk drifting from — how a closed notice looks.
 */
const CLOSED_NOTICE_CLASS =
  "mt-4 p-3 rounded border border-red-200 bg-red-50 text-sm font-medium text-red-700 text-center";

export default function FixedDateDealSalesGate({
  deal,
  locale = "nl",
  showClosedNotice = false,
  className = "",
  children,
}) {
  const fixedDateClosed = useFixedDateVoucherUnavailable(deal);
  // Fixronde 1 fe#1934: ook de verkoopstop van de config (182 na 23-12-2026);
  // de page geeft daarvoor `deals_id` in de projectie mee.
  const saleClosed = useVoucherSaleClosed(deal);
  const { t } = getTranslations(locale);

  if (fixedDateClosed || saleClosed) {
    if (!showClosedNotice) return null;
    return (
      <p
        data-testid="fixed-date-deal-sale-closed"
        role="status"
        className={`${CLOSED_NOTICE_CLASS} ${className}`.trim()}
      >
        {t(fixedDateClosed ? "booking.error.fixed_date_unavailable" : "voucher.sale_closed")}
      </p>
    );
  }

  return <>{children}</>;
}
